/**
 * What the live Azure data says about a VM size (offered in the region, restricted, vCPU quota) and how it relates to
 * the T-shirt size's configured VM. Pure helpers: the callers fetch the size with the live catalog.
 */
import type { AzureVmSize, VmSizing } from '@/types'

export interface SizeCheck {
  /** Blocks the request (it would fail in Azure). */
  error?: string
  /** Worth knowing, does not block. */
  warning?: string
}

const gb = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1))

/** The quota shortfall as a sentence, or '' when the size fits (or nothing is known). */
export function quotaShortfall(size: AzureVmSize, region: string): string {
  const q = size.quota
  if (!q || q.enough !== false) return ''
  const parts = [
    q.family_free != null && q.family_free < size.vcpus ? `the ${q.family || 'VM'} family has ${q.family_free} free` : '',
    q.regional_free != null && q.regional_free < size.vcpus ? `the region has ${q.regional_free} free` : '',
  ].filter(Boolean)
  return `Not enough vCPU quota in ${region}: ${size.name} needs ${size.vcpus} vCPUs but ${parts.join(' and ')}.`
}

/**
 * The size a requester picked in the wizard: an error when Azure cannot create it (not offered, restricted, no quota),
 * otherwise a note when it differs from the VM the T-shirt size is configured with (null = the size has no VM profile).
 */
export function checkVmSize(size: AzureVmSize | undefined, region: string, configured: VmSizing | null): SizeCheck {
  if (!size) return {}
  if (!size.available) {
    const why = size.restrictions.length ? ` (${size.restrictions.join(', ')})` : ''
    return { error: `${size.name} is not available in ${region} for this subscription${why}. Pick another size or region.` }
  }
  const shortfall = quotaShortfall(size, region)
  if (shortfall) return { error: `${shortfall} Pick a smaller size or another family, or ask for a quota increase.` }
  if (configured?.vm_size && size.name.toLowerCase() !== configured.vm_size.toLowerCase()) {
    const smaller = size.vcpus < configured.vcpu || size.memory_gb < configured.memory_gb
    return {
      warning: `Different from this T-shirt size: it is configured as ${configured.vm_size} (${configured.vcpu} vCPU / ${gb(configured.memory_gb)} GB); `
        + `you picked ${size.name} (${size.vcpus} vCPU / ${gb(size.memory_gb)} GB).`
        + (smaller ? ' It is smaller than the size assumes, so the user count may not be met.' : ''),
    }
  }
  return {}
}

export interface AdminSizeCheck {
  tone: 'ok' | 'warn'
  lines: string[]
  /** Azure's own vCPU / memory for the size, to offer as a fix when the profile disagrees. */
  azure?: { vcpus: number; memory_gb: number }
}

/** The VM size an administrator typed for a T-shirt size, checked against Azure in a reference region. */
export function checkAdminVmSize(
  name: string, size: AzureVmSize | undefined, region: string, entered: { vcpu: string; memory: string },
): AdminSizeCheck | null {
  const wanted = name.trim()
  if (!wanted) return null
  if (!size) return { tone: 'warn', lines: [`${wanted} is not offered in ${region} for this subscription. Check the name.`] }
  const lines: string[] = []
  let tone: AdminSizeCheck['tone'] = 'ok'
  if (!size.available) {
    tone = 'warn'
    lines.push(`${size.name} is restricted in ${region} for this subscription${size.restrictions.length ? ` (${size.restrictions.join(', ')})` : ''}.`)
  } else {
    lines.push(`Offered in ${region}: ${size.vcpus} vCPU / ${gb(size.memory_gb)} GB.`)
  }
  const shortfall = quotaShortfall(size, region)
  if (shortfall) {
    tone = 'warn'
    lines.push(`${shortfall} Requests for this size will fail until the quota is raised or another size is used.`)
  } else if (size.quota && size.available) {
    const free = [size.quota.family_free, size.quota.regional_free].filter((n): n is number => n != null)
    if (free.length) lines.push(`vCPU quota: ${Math.min(...free)} free (needs ${size.vcpus}).`)
  }
  const vcpu = Number(entered.vcpu.trim())
  const memory = Number(entered.memory.trim())
  const differs = (entered.vcpu.trim() && vcpu !== size.vcpus) || (entered.memory.trim() && memory !== size.memory_gb)
  if (differs) {
    tone = 'warn'
    lines.push(`Azure lists ${size.vcpus} vCPU / ${gb(size.memory_gb)} GB for ${size.name}; the profile says ${entered.vcpu.trim() || '—'} / ${entered.memory.trim() || '—'}.`)
  }
  return { tone, lines, azure: { vcpus: size.vcpus, memory_gb: size.memory_gb } }
}

// ─── Image ↔ VM size compatibility (live facts from Azure) ────────────────────────────────────────────────

export interface ImageFacts {
  label?: string
  /** "V1" / "V2", "" when unknown. */
  hyperv_generation?: string
  architecture?: string
}

export interface SizeFacts {
  name: string
  hyperv_generations?: string[]
  architecture?: string
}

const genLabel = (g: string) => (g === 'V2' ? 'Generation 2' : 'Generation 1')

/**
 * '' when the image and the VM size can be used together; otherwise why not. A Generation 2 image (…-g2, …-gensecond, Azure
 * Edition) needs a size that supports Generation 2, a Generation 1 image a size that supports Generation 1, and an Arm64
 * size cannot run an x64 image. Unknown facts never block.
 */
export function imageSizeProblem(image: ImageFacts | undefined, size: SizeFacts | undefined): string {
  if (!image || !size) return ''
  const label = image.label || 'This image'
  const generation = (image.hyperv_generation || '').toUpperCase()
  const supported = (size.hyperv_generations ?? []).map((g) => g.toUpperCase())
  if (generation && supported.length > 0 && !supported.includes(generation)) {
    const sizeGen = supported.map(genLabel).join(' / ')
    const fix = generation === 'V2'
      ? 'Pick a Generation 1 image or a size that supports Generation 2.'
      : 'Pick a Generation 2 image (its name ends -g2 or -gensecond, or it is an Azure Edition) or a size that supports Generation 1.'
    return `${label} is a ${genLabel(generation)} image, but ${size.name} supports ${sizeGen} only. ${fix}`
  }
  const imageArch = (image.architecture || '').toLowerCase()
  const sizeArch = (size.architecture || '').toLowerCase()
  if (imageArch && sizeArch && imageArch !== sizeArch) {
    return `${label} is for ${image.architecture} machines, but ${size.name} is an ${size.architecture} size. Pick a size or an image with the same architecture.`
  }
  return ''
}
